import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete, Avatar, Box, Grid, MenuItem, Stack, TextField, Typography, Paper, alpha, IconButton, Button,
} from '@mui/material';
import { LocalShipping, Handyman, Add, Remove } from '@mui/icons-material';
import LocationSearchField from '../maps/LocationSearchField';
import DispatchRouteMap from './DispatchRouteMap';
import DispatchFoodMenuPicker, {
  type DispatchRestaurant, type FoodCartLine, type FoodMenuItem,
} from './DispatchFoodMenuPicker';
import DispatchPorterOptions, { type PorterBookingState } from './DispatchPorterOptions';
import DispatchOnDemandServicePicker, {
  type OnDemandCartLine, type OnDemandPackage,
} from './DispatchOnDemandServicePicker';
import { useCurrentLocation } from '../../hooks/useCurrentLocation';
import type { LocationResult } from '../../services/locationService';
import { useDispatchCustomerPhone } from './DispatchCustomerPhoneStep';
import DispatchPartnerAssignStep from './DispatchPartnerAssignStep';
import { DispatchSummaryCard } from './DispatchStepNav';
import { bookPanelSx } from './dispatchTheme';
import { BookSectionTitle } from './DispatchBookChrome';
import type { DispatchMode } from './dispatchBookUtils';
import type { BookMode } from './DispatcherBookDrawer';
import {
  placeFoodOrder, placeGroceryOrder, placePorterOrder, bookOnDemand,
  getRestaurants, getRestaurantMenu, getGroceryStores, getGroceryProducts,
  getPorterVehicles, getPorterCategories, getPorterPackages,
  listOnDemandPackagesAdmin, listOnDemandCategories, getOnDemandSlots,
} from '../../services/businessService';
import { autoAssignJob, manualAssignJob, ENTITY_TYPE_MAP } from '../../services/dispatcherService';
import { formatMoney } from '../../utils/currency';
import { resolveAssetUrl } from '../../utils/assetUrl';

const emptyPorter: PorterBookingState = {
  vehicleTypeId: '',
  categoryId: '',
  packageId: '',
  weightKg: '20',
  helpersCount: '0',
  specialHandling: [],
  insuranceOpted: false,
  declaredValue: '0',
  goodsDescription: '',
  receiverName: '',
  receiverPhone: '',
};

interface DispatchServiceBookWizardProps {
  mode: Exclude<BookMode, 'ride' | null>;
  accent: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
  setLoading: (v: boolean) => void;
  setCanSubmit: (v: boolean) => void;
  registerSubmit: (fn: () => Promise<void>) => void;
}

function packageIdOf(row: { _id?: string; id?: string }): string {
  return String(row._id || row.id || '');
}

export default function DispatchServiceBookWizard({
  mode, accent, onSuccess, onError, setLoading, setCanSubmit, registerSubmit,
}: DispatchServiceBookWizardProps) {
  const customerStep = useDispatchCustomerPhone();
  const [form, setForm] = useState<Record<string, string>>({ paymentMethod: 'cash', quantity: '1', city: 'Bangalore', weightKg: '20', helpersCount: '0' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [dispatchMode, setDispatchMode] = useState<DispatchMode>('AUTOMATIC');
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [deliveryLoc, setDeliveryLoc] = useState<LocationResult | null>(null);
  const [pickupLoc, setPickupLoc] = useState<LocationResult | null>(null);
  const [dropoffLoc, setDropoffLoc] = useState<LocationResult | null>(null);
  const [mapFocusPin, setMapFocusPin] = useState<'pickup' | 'dropoff'>('pickup');
  const [foodCart, setFoodCart] = useState<FoodCartLine[]>([]);
  const [groceryCart, setGroceryCart] = useState<Array<{ productId: string; quantity: number }>>([]);
  const [porter, setPorter] = useState<PorterBookingState>(emptyPorter);
  const [serviceCart, setServiceCart] = useState<OnDemandCartLine[]>([]);
  const geo = useCurrentLocation(true);

  const { data: restaurants = [] } = useQuery({
    queryKey: ['dispatcher-restaurants'],
    queryFn: () => getRestaurants(),
    enabled: mode === 'food',
  });

  const restaurantId = form.restaurantId || restaurants[0]?._id;
  const { data: menu = [] } = useQuery({
    queryKey: ['dispatcher-menu', restaurantId],
    queryFn: () => getRestaurantMenu(restaurantId),
    enabled: mode === 'food' && !!restaurantId,
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['dispatcher-stores'],
    queryFn: () => getGroceryStores(),
    enabled: mode === 'grocery',
  });

  const storeId = form.storeId || stores[0]?._id;
  const { data: products = [] } = useQuery({
    queryKey: ['dispatcher-products', storeId],
    queryFn: () => getGroceryProducts(storeId),
    enabled: mode === 'grocery' && !!storeId,
  });

  const { data: porterVehicles = [] } = useQuery({
    queryKey: ['dispatcher-porter-vehicles'],
    queryFn: () => getPorterVehicles(),
    enabled: mode === 'porter',
  });

  const { data: porterCategories = [] } = useQuery({
    queryKey: ['dispatcher-porter-categories'],
    queryFn: () => getPorterCategories(),
    enabled: mode === 'porter',
  });

  const { data: porterPackages = [] } = useQuery({
    queryKey: ['dispatcher-porter-packages'],
    queryFn: () => getPorterPackages(),
    enabled: mode === 'porter',
  });

  const { data: onDemandPackagesRaw } = useQuery({
    queryKey: ['dispatcher-on-demand-packages'],
    queryFn: () => listOnDemandPackagesAdmin({ limit: 200 }),
    enabled: mode === 'on_demand',
  });

  const { data: onDemandCategories = [] } = useQuery({
    queryKey: ['dispatcher-on-demand-categories'],
    queryFn: async () => {
      const raw = await listOnDemandCategories();
      const list = Array.isArray(raw) ? raw : [];
      const flat: typeof list = [];
      for (const row of list as Array<Record<string, unknown>>) {
        flat.push(row as (typeof list)[number]);
        const kids = (row.children || row.subcategories || row.subCategories) as Array<Record<string, unknown>> | undefined;
        if (Array.isArray(kids)) {
          for (const child of kids) {
            flat.push({
              ...child,
              parentId: child.parentId || row.id || row._id,
              parentCode: child.parentCode || row.code,
            } as (typeof list)[number]);
          }
        }
      }
      return flat;
    },
    enabled: mode === 'on_demand',
  });

  const onDemandPackages = useMemo(() => {
    const raw = onDemandPackagesRaw as { data?: unknown[]; packages?: unknown[] } | unknown[] | undefined;
    const list = Array.isArray(raw) ? raw : (raw?.data || raw?.packages || []);
    return (list as OnDemandPackage[]).filter((p) => packageIdOf(p));
  }, [onDemandPackagesRaw]);

  const scheduledDate = (form.scheduledAt || '').slice(0, 10);
  const primaryServiceId = serviceCart[0]?.packageId || packageIdOf(onDemandPackages[0] || {});
  const { data: slotsData } = useQuery({
    queryKey: ['dispatcher-on-demand-slots', primaryServiceId, scheduledDate, deliveryLoc?.lat, deliveryLoc?.lng],
    queryFn: () => getOnDemandSlots({
      date: scheduledDate,
      lat: deliveryLoc?.lat,
      lng: deliveryLoc?.lng,
      durationMin: onDemandPackages.find((p) => packageIdOf(p) === primaryServiceId)?.durationMin,
    }),
    enabled: mode === 'on_demand' && !!primaryServiceId && !!scheduledDate,
  });

  const slots = slotsData?.slots || [];

  useEffect(() => {
    customerStep.reset();
    setDispatchMode('AUTOMATIC');
    setSelectedPartnerId('');
    setDeliveryLoc(null);
    setPickupLoc(null);
    setDropoffLoc(null);
    setFoodCart([]);
    setGroceryCart([]);
    setServiceCart([]);
    setPorter(emptyPorter);
    setFieldErrors({});
    const soon = new Date(Date.now() + 60 * 60 * 1000);
    const local = new Date(soon.getTime() - soon.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setForm({
      paymentMethod: 'cash',
      quantity: '1',
      city: 'Bangalore',
      scheduledAt: local,
      restaurantId: restaurants[0]?._id || '',
      storeId: stores[0]?._id || '',
    });
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === 'food' && restaurants[0]?._id && !form.restaurantId) {
      setForm((f) => ({ ...f, restaurantId: restaurants[0]._id }));
    }
  }, [mode, restaurants, form.restaurantId]);

  useEffect(() => {
    if (mode === 'grocery' && stores[0]?._id && !form.storeId) {
      setForm((f) => ({ ...f, storeId: stores[0]._id }));
    }
  }, [mode, stores, form.storeId]);

  useEffect(() => {
    if (mode !== 'porter' || !porterVehicles[0]?._id) return;
    if (!porter.vehicleTypeId) {
      setPorter((p) => ({ ...p, vehicleTypeId: porterVehicles[0]._id }));
    }
  }, [mode, porterVehicles, porter.vehicleTypeId]);

  useEffect(() => {
    if (mode === 'porter') {
      if (pickupLoc || !geo.address) return;
      setPickupLoc({ ...geo.address, label: 'My Current Location' });
      if (geo.address.city) setForm((f) => ({ ...f, city: geo.address!.city || f.city }));
      return;
    }
    if (deliveryLoc || !geo.address) return;
    setDeliveryLoc(geo.address);
    if (geo.address.city) setForm((f) => ({ ...f, city: geo.address!.city || f.city }));
  }, [geo.address, deliveryLoc, pickupLoc, mode]);

  const collectErrors = useCallback((): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!customerStep.isReady) next.customer = 'Look up or create a customer first';
    if (mode === 'food') {
      if (!(form.restaurantId || restaurantId)) next.restaurantId = 'Restaurant is required';
      if (!foodCart.length) next.cart = 'Add at least one menu item';
      if (!deliveryLoc?.address?.trim()) next.delivery = 'Delivery address is required';
    }
    if (mode === 'grocery') {
      if (!(form.storeId || storeId)) next.storeId = 'Store is required';
      if (!groceryCart.length) next.cart = 'Add at least one product';
      if (!deliveryLoc?.address?.trim()) next.delivery = 'Delivery address is required';
    }
    if (mode === 'porter') {
      if (!pickupLoc?.address?.trim()) next.pickup = 'Pickup location is required';
      if (!dropoffLoc?.address?.trim()) next.dropoff = 'Drop location is required';
      if (!(porter.vehicleTypeId || porterVehicles[0]?._id)) next.vehicleTypeId = 'Vehicle is required';
    }
    if (mode === 'on_demand') {
      if (!serviceCart.length) next.cart = 'Add at least one service package';
      if (!deliveryLoc?.address?.trim()) next.delivery = 'Service address is required';
      if (!form.scheduledAt) next.scheduledAt = 'Schedule is required';
    }
    if (!form.paymentMethod) next.paymentMethod = 'Payment method is required';
    if (dispatchMode === 'MANUAL' && !selectedPartnerId) next.partner = 'Select a partner for manual dispatch';
    return next;
  }, [customerStep.isReady, mode, form, restaurantId, foodCart, groceryCart, serviceCart, storeId, deliveryLoc, pickupLoc, dropoffLoc, porter, porterVehicles, dispatchMode, selectedPartnerId]);

  useEffect(() => {
    setCanSubmit(Object.keys(collectErrors()).length === 0);
  }, [collectErrors, setCanSubmit]);

  const assignAfterCreate = async (serviceType: string, entityType: string, entityId: string) => {
    if (dispatchMode === 'MANUAL' && selectedPartnerId) {
      await manualAssignJob({ serviceType, entityType, entityId, partnerId: selectedPartnerId, mode: 'manual' });
    } else if (dispatchMode === 'AUTOMATIC') {
      try { await autoAssignJob({ serviceType, entityType, entityId }); } catch { /* cascade / searching */ }
    }
  };

  const handleSubmit = useCallback(async () => {
    const nextErrors = collectErrors();
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      onError(Object.values(nextErrors)[0]);
      return;
    }
    setLoading(true);
    onError('');
    try {
      const customerId = await customerStep.resolveCustomerId();
      let entityId = '';
      let serviceType = '';
      let entityType = '';

      if (mode === 'food') {
        if (!foodCart.length) throw new Error('Add at least one menu item');
        const order = await placeFoodOrder({
          customerId,
          restaurantId: form.restaurantId || restaurantId,
          items: foodCart.map((line) => ({ menuItemId: line.menuItemId, quantity: line.quantity })),
          deliveryAddress: {
            street: deliveryLoc!.address,
            city: deliveryLoc!.city || form.city || 'Bangalore',
            country: 'IN',
            lat: deliveryLoc!.lat,
            lng: deliveryLoc!.lng,
          },
          paymentMethod: form.paymentMethod || 'cash',
        });
        entityId = order._id;
        serviceType = 'food';
        entityType = ENTITY_TYPE_MAP.food;
      } else if (mode === 'grocery') {
        if (!groceryCart.length) throw new Error('Add at least one product');
        const order = await placeGroceryOrder({
          customerId,
          storeId: form.storeId || storeId,
          items: groceryCart.map((line) => ({ productId: line.productId, quantity: line.quantity })),
          deliveryAddress: {
            street: deliveryLoc!.address,
            city: deliveryLoc!.city || form.city || 'Bangalore',
            country: 'IN',
            lat: deliveryLoc!.lat,
            lng: deliveryLoc!.lng,
          },
          paymentMethod: form.paymentMethod || 'cash',
        });
        entityId = order._id;
        serviceType = 'grocery';
        entityType = ENTITY_TYPE_MAP.grocery;
      } else if (mode === 'porter') {
        const vehicleTypeId = porter.vehicleTypeId || porterVehicles[0]?._id;
        const categoryId = porter.categoryId;
        const packageId = porter.packageId;
        const pkg = porterPackages.find((p) => p._id === packageId);
        const cat = porterCategories.find((c) => c._id === categoryId);
        const pkgName = pkg?.name || cat?.name || 'Goods';
        const weightKg = Number(porter.weightKg) || pkg?.weightKg || 20;
        const order = await placePorterOrder({
          customerId,
          vehicleTypeId,
          helpersCount: Number(porter.helpersCount) || 0,
          paymentMethod: form.paymentMethod || 'cash',
          bookingType: 'instant',
          insuranceOpted: porter.insuranceOpted,
          declaredValue: porter.insuranceOpted ? Number(porter.declaredValue) || 0 : 0,
          specialHandling: porter.specialHandling,
          categoryIds: categoryId ? [categoryId] : [],
          goodsDescription: porter.goodsDescription || 'Dispatcher booking',
          receiverName: porter.receiverName || undefined,
          receiverPhone: porter.receiverPhone || undefined,
          packages: [{
            packageId: packageId || undefined,
            name: pkgName,
            weightKg,
            categoryId: categoryId || undefined,
            specialHandling: porter.specialHandling,
            declaredValue: porter.insuranceOpted ? Number(porter.declaredValue) || 0 : 0,
          }],
          stops: [
            {
              type: 'pickup', lat: pickupLoc!.lat, lng: pickupLoc!.lng, address: pickupLoc!.address,
              contactPhone: porter.receiverPhone || undefined,
            },
            {
              type: 'drop', lat: dropoffLoc!.lat, lng: dropoffLoc!.lng, address: dropoffLoc!.address,
              contactPhone: porter.receiverPhone || undefined, contactName: porter.receiverName || undefined,
            },
          ],
        });
        entityId = order._id;
        serviceType = 'porter';
        entityType = ENTITY_TYPE_MAP.porter;
      } else {
        if (!serviceCart.length) throw new Error('Add at least one service package');
        const scheduledAt = form.slotIso || form.scheduledAt;
        const address = {
          line1: deliveryLoc!.address,
          lat: deliveryLoc!.lat,
          lng: deliveryLoc!.lng,
          location: { type: 'Point', coordinates: [deliveryLoc!.lng, deliveryLoc!.lat] },
        };
        const createdIds: string[] = [];
        for (const line of serviceCart) {
          for (let i = 0; i < line.quantity; i += 1) {
            const booking = await bookOnDemand({
              customerId,
              packageId: line.packageId,
              scheduledAt: new Date(scheduledAt).toISOString(),
              address,
              paymentMethod: form.paymentMethod || 'cash',
              bookingType: 'instant',
              offerMode: 'broadcast',
            });
            const created = (booking?.booking || booking) as { _id?: string };
            const id = String(created?._id || booking?._id || '');
            if (id) createdIds.push(id);
          }
        }
        entityId = createdIds[0] || '';
        serviceType = 'on_demand';
        entityType = ENTITY_TYPE_MAP.on_demand;
        for (const id of createdIds.slice(1)) {
          try { await assignAfterCreate(serviceType, entityType, id); } catch { /* continue */ }
        }
      }

      if (entityId && serviceType) await assignAfterCreate(serviceType, entityType, entityId);
      onSuccess();
    } catch (e: unknown) {
      onError((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (e as Error)?.message || 'Booking failed');
    } finally {
      setLoading(false);
    }
  }, [mode, customerStep, form, foodCart, groceryCart, serviceCart, porter, restaurantId, storeId, dispatchMode, selectedPartnerId, deliveryLoc, pickupLoc, dropoffLoc, porterVehicles, porterPackages, porterCategories, collectErrors, setLoading, onSuccess, onError]);

  useEffect(() => { registerSubmit(handleSubmit); }, [handleSubmit, registerSubmit]);

  const summaryRows = useMemo(() => {
    const rows: { label: string; value: string }[] = [];
    if (customerStep.customer) {
      rows.push({ label: 'Customer', value: [customerStep.customer.firstName, customerStep.customer.lastName].filter(Boolean).join(' ') || customerStep.customer.phone || '—' });
    }
    if (mode === 'food') {
      const r = restaurants.find((x: { _id: string }) => x._id === (form.restaurantId || restaurantId));
      if (r) rows.push({ label: 'Restaurant', value: r.name });
      if (foodCart.length) {
        const labels = foodCart.map((line) => {
          const item = (menu as FoodMenuItem[]).find((m) => m._id === line.menuItemId);
          return `${item?.name || 'Item'} × ${line.quantity}`;
        });
        rows.push({ label: 'Items', value: labels.join(', ') });
      }
      if (deliveryLoc) rows.push({ label: 'Delivery', value: deliveryLoc.address });
    }
    if (mode === 'grocery') {
      const s = stores.find((x: { _id: string }) => x._id === (form.storeId || storeId));
      if (s) rows.push({ label: 'Store', value: s.name });
      if (groceryCart.length) {
        const labels = groceryCart.map((line) => {
          const p = products.find((x: { _id: string; name: string }) => x._id === line.productId);
          return `${p?.name || 'Product'} × ${line.quantity}`;
        });
        rows.push({ label: 'Items', value: labels.join(', ') });
      }
      if (deliveryLoc) rows.push({ label: 'Delivery', value: deliveryLoc.address });
    }
    if (mode === 'porter') {
      const v = porterVehicles.find((x) => x._id === (porter.vehicleTypeId || porterVehicles[0]?._id));
      if (v) rows.push({ label: 'Vehicle', value: v.name });
      if (Number(porter.helpersCount) > 0) rows.push({ label: 'Helpers', value: porter.helpersCount });
      if (porter.specialHandling.length) rows.push({ label: 'Handling', value: porter.specialHandling.join(', ') });
      if (porter.insuranceOpted) rows.push({ label: 'Insurance', value: `Yes · ${porter.declaredValue}` });
      if (pickupLoc) rows.push({ label: 'Pickup', value: pickupLoc.address });
      if (dropoffLoc) rows.push({ label: 'Drop', value: dropoffLoc.address });
    }
    if (mode === 'on_demand') {
      if (serviceCart.length) {
        const labels = serviceCart.map((line) => {
          const pkg = onDemandPackages.find((p) => packageIdOf(p) === line.packageId);
          return `${pkg?.name || 'Service'} × ${line.quantity}`;
        });
        rows.push({ label: 'Services', value: labels.join(', ') });
      }
      if (deliveryLoc) rows.push({ label: 'Address', value: deliveryLoc.address });
      if (form.scheduledAt) rows.push({ label: 'Schedule', value: form.scheduledAt.replace('T', ' ') });
    }
    rows.push({ label: 'Payment', value: (form.paymentMethod || 'cash').toUpperCase() });
    rows.push({ label: 'Dispatch', value: dispatchMode === 'MANUAL' ? 'Manual assign' : 'Automatic' });
    return rows;
  }, [customerStep.customer, mode, form, restaurants, menu, stores, products, restaurantId, storeId, foodCart, groceryCart, serviceCart, porter, dispatchMode, deliveryLoc, pickupLoc, dropoffLoc, porterVehicles, onDemandPackages]);

  const partnerType = mode === 'on_demand' ? 'service_provider' : 'delivery_partner';
  const mapCoords = mode === 'porter'
    ? (pickupLoc || geo.coords)
    : (deliveryLoc || geo.coords);
  const assignCoords = mode === 'porter'
    ? (pickupLoc ? { lat: pickupLoc.lat, lng: pickupLoc.lng } : geo.coords)
    : (deliveryLoc ? { lat: deliveryLoc.lat, lng: deliveryLoc.lng } : geo.coords);

  const payField = (
    <TextField select fullWidth size="small" required label="Payment" value={form.paymentMethod || 'cash'}
      error={!!fieldErrors.paymentMethod} helperText={fieldErrors.paymentMethod}
      onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}>
      <MenuItem value="cash">Cash</MenuItem>
      <MenuItem value="wallet">Wallet</MenuItem>
      <MenuItem value="online">Online</MenuItem>
    </TextField>
  );

  const orderFields = (
    <>
      {mode === 'food' && (
        <DispatchFoodMenuPicker
          accent={accent}
          restaurants={restaurants as DispatchRestaurant[]}
          restaurantId={form.restaurantId || restaurantId || ''}
          onRestaurantChange={(id) => setForm({ ...form, restaurantId: id })}
          menu={menu as FoodMenuItem[]}
          cart={foodCart}
          onCartChange={setFoodCart}
          fieldErrors={fieldErrors}
        />
      )}

      {mode === 'grocery' && (
        <Stack spacing={1.15} sx={{ overflow: 'visible', pt: 0.5 }}>
          <Autocomplete
            size="small"
            options={stores as Array<{ _id: string; name: string; logo?: string; coverImage?: string }>}
            value={(stores as Array<{ _id: string; name: string; logo?: string; coverImage?: string }>).find((s) => s._id === (form.storeId || storeId)) || null}
            onChange={(_, v) => { setForm({ ...form, storeId: v?._id || '' }); setGroceryCart([]); }}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option._id} sx={{ display: 'flex !important', gap: 1, alignItems: 'center' }}>
                <Avatar
                  src={resolveAssetUrl(option.logo || option.coverImage || '') || undefined}
                  variant="rounded"
                  sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 800 }}
                >
                  {(option.name || 'S').slice(0, 1)}
                </Avatar>
                <Typography variant="body2" fontWeight={700}>{option.name}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Store"
                placeholder="Search store…"
                error={!!fieldErrors.storeId}
                helperText={fieldErrors.storeId}
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Avatar
                        src={resolveAssetUrl(
                          ((stores as Array<{ _id: string; logo?: string; coverImage?: string }>).find((s) => s._id === (form.storeId || storeId))?.logo)
                          || ((stores as Array<{ _id: string; logo?: string; coverImage?: string }>).find((s) => s._id === (form.storeId || storeId))?.coverImage)
                          || '',
                        ) || undefined}
                        variant="rounded"
                        sx={{ width: 24, height: 24, mr: 1, flexShrink: 0, fontSize: 11 }}
                      >
                        S
                      </Avatar>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { alignItems: 'center', minHeight: 40 },
                  '& .MuiInputBase-input': { py: '8.5px' },
                }}
              />
            )}
          />
          {fieldErrors.cart && (
            <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.cart}</Typography>
          )}
          <Box sx={{
            maxHeight: 240, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: '#f8fafc', p: 1,
          }}>
            <Grid container spacing={1}>
              {(products as Array<{ _id: string; name: string; price?: number; image?: string }>).map((p) => {
                const qty = groceryCart.find((c) => c.productId === p._id)?.quantity || 0;
                const img = resolveAssetUrl(p.image || '');
                const setQty = (quantity: number) => {
                  const next = groceryCart.filter((c) => c.productId !== p._id);
                  if (quantity > 0) next.push({ productId: p._id, quantity });
                  setGroceryCart(next);
                };
                return (
                  <Grid item xs={12} sm={6} key={p._id}>
                    <Paper elevation={0} sx={{
                      p: 1, display: 'flex', gap: 1, borderRadius: '10px',
                      border: `1px solid ${qty > 0 ? alpha(accent, 0.45) : '#e2e8f0'}`,
                      bgcolor: qty > 0 ? alpha(accent, 0.06) : '#fff',
                    }}>
                      <Box sx={{
                        width: 52, height: 52, borderRadius: '8px', flexShrink: 0, bgcolor: '#e2e8f0',
                        backgroundImage: img ? `url(${img})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'text.secondary',
                      }}>
                        {!img ? 'ITEM' : null}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>{p.name}</Typography>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.5}>
                          <Typography variant="caption" fontWeight={800}>{p.price != null ? formatMoney(p.price) : '—'}</Typography>
                          {qty === 0 ? (
                            <Button size="small" variant="outlined" onClick={() => setQty(1)}
                              sx={{ minWidth: 0, px: 1, py: 0.1, borderRadius: '8px', borderColor: accent, color: accent, fontWeight: 800, fontSize: 11 }}>
                              ADD
                            </Button>
                          ) : (
                            <Stack direction="row" alignItems="center" spacing={0.2} sx={{ border: `1px solid ${alpha(accent, 0.35)}`, borderRadius: '8px', bgcolor: '#fff' }}>
                              <IconButton size="small" onClick={() => setQty(qty - 1)} sx={{ color: accent }}><Remove fontSize="small" /></IconButton>
                              <Typography variant="caption" fontWeight={800} sx={{ minWidth: 14, textAlign: 'center' }}>{qty}</Typography>
                              <IconButton size="small" onClick={() => setQty(qty + 1)} sx={{ color: accent }}><Add fontSize="small" /></IconButton>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Stack>
      )}

      {mode === 'porter' && (
        <Box sx={{ maxHeight: 360, overflow: 'auto', pr: 0.5, pt: 0.75 }}>
          <DispatchPorterOptions
            accent={accent}
            vehicles={porterVehicles}
            categories={porterCategories}
            packages={porterPackages}
            value={porter}
            onChange={setPorter}
            pickup={pickupLoc ? { lat: pickupLoc.lat, lng: pickupLoc.lng, address: pickupLoc.address } : null}
            dropoff={dropoffLoc ? { lat: dropoffLoc.lat, lng: dropoffLoc.lng, address: dropoffLoc.address } : null}
            fieldErrors={fieldErrors}
          />
        </Box>
      )}

      {mode === 'on_demand' && (
        <Box sx={{ maxHeight: 380, overflow: 'auto', pr: 0.5, pt: 0.75 }}>
          <DispatchOnDemandServicePicker
            accent={accent}
            categories={onDemandCategories}
            packages={onDemandPackages}
            cart={serviceCart}
            onCartChange={setServiceCart}
            scheduledAt={form.scheduledAt || ''}
            slotIso={form.slotIso || ''}
            slots={slots}
            onScheduleChange={(scheduledAt, slotIso) => setForm({ ...form, scheduledAt, slotIso: slotIso || '' })}
            fieldErrors={fieldErrors}
          />
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
      {customerStep.panel}
      {fieldErrors.customer && (
        <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.customer}</Typography>
      )}

      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(300px, 0.8fr)' },
        gap: 1.25,
        overflow: 'hidden',
      }}>
        <Paper elevation={0} sx={{ ...bookPanelSx({ p: 1.75, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', gap: 1.15 }) }}>
          {orderFields}
          <BookSectionTitle
            icon={mode === 'on_demand' ? <Handyman fontSize="small" /> : <LocalShipping fontSize="small" />}
            title={mode === 'porter' ? 'Pickup & Drop' : mode === 'on_demand' ? 'Service Location' : 'Delivery Location'}
            accent={accent}
          />
          <Grid container spacing={1}>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="City" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Grid>
            {mode === 'porter' ? (
              <>
                <Grid item xs={12} sm={4}>
                  <LocationSearchField
                    label="Pickup"
                    value={pickupLoc}
                    onChange={(loc) => {
                      setPickupLoc(loc);
                      setMapFocusPin('pickup');
                    }}
                    bias={geo.coords}
                    city={form.city}
                    accent="#22c55e"
                    required
                    error={!!fieldErrors.pickup}
                    helperText={fieldErrors.pickup}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <LocationSearchField
                    label="Drop"
                    value={dropoffLoc}
                    onChange={(loc) => {
                      setDropoffLoc(loc);
                      setMapFocusPin('dropoff');
                    }}
                    bias={geo.coords}
                    city={form.city}
                    accent={accent}
                    required
                    error={!!fieldErrors.dropoff}
                    helperText={fieldErrors.dropoff}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={9}>
                <LocationSearchField
                  label={mode === 'on_demand' ? 'Service Address' : 'Delivery Address'}
                  value={deliveryLoc}
                  onChange={(loc) => {
                    setDeliveryLoc(loc);
                    setMapFocusPin('pickup');
                  }}
                  bias={geo.coords}
                  city={form.city}
                  accent={accent}
                  required
                  error={!!fieldErrors.delivery}
                  helperText={fieldErrors.delivery}
                />
              </Grid>
            )}
          </Grid>
          <Box sx={{ flex: 1, minHeight: 140 }}>
            <DispatchRouteMap
              height={200}
              center={mapCoords ? { lat: 'lat' in mapCoords ? mapCoords.lat : 0, lng: 'lng' in mapCoords ? mapCoords.lng : 0 } : undefined}
              markers={[
                ...(geo.coords ? [{ id: 'cur', lat: geo.coords.lat, lng: geo.coords.lng, label: 'You', color: '#2563eb', kind: 'current' as const }] : []),
                ...(mode === 'porter'
                  ? [
                      ...(pickupLoc ? [{ id: 'pick', lat: pickupLoc.lat, lng: pickupLoc.lng, label: 'Pickup', color: '#22c55e', kind: 'pickup' as const }] : []),
                      ...(dropoffLoc ? [{ id: 'drop', lat: dropoffLoc.lat, lng: dropoffLoc.lng, label: 'Drop', color: accent, kind: 'dropoff' as const }] : []),
                    ]
                  : (deliveryLoc ? [{ id: 'del', lat: deliveryLoc.lat, lng: deliveryLoc.lng, label: mode === 'on_demand' ? 'Service' : 'Delivery', color: accent, kind: 'pickup' as const }] : [])),
              ]}
              activePin={mode === 'porter' ? mapFocusPin : 'pickup'}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...bookPanelSx({ p: 1.75, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', gap: 1.15 }) }}>
          <DispatchSummaryCard title="Summary" rows={summaryRows} accent={accent} />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DispatchPartnerAssignStep
              compact
              partnerType={partnerType}
              dispatchMode={dispatchMode}
              onDispatchModeChange={setDispatchMode}
              selectedPartnerId={selectedPartnerId}
              onPartnerSelect={setSelectedPartnerId}
              coordinates={assignCoords}
              enabled={customerStep.isReady}
            />
          </Box>
          {fieldErrors.partner && (
            <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.partner}</Typography>
          )}
          {payField}
        </Paper>
      </Box>
    </Box>
  );
}
